import { Outlet, createRootRoute, createRoute, createRouter, lazyRouteComponent, type RouterHistory } from '@tanstack/react-router';
import { AuthedLayout, NotFound, PublicLayout } from './layouts';
import { parseFilters } from '@/features/transactions/filters';

const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFound });

const publicRoute = createRoute({ getParentRoute: () => rootRoute, id: 'public', component: PublicLayout });
const loginRoute = createRoute({ getParentRoute: () => publicRoute, path: '/login', component: lazyRouteComponent(() => import('@/features/auth/LoginPage'), 'LoginPage') });
const registerRoute = createRoute({ getParentRoute: () => publicRoute, path: '/register', component: lazyRouteComponent(() => import('@/features/auth/RegisterPage'), 'RegisterPage') });
const forgotRoute = createRoute({ getParentRoute: () => publicRoute, path: '/forgot-password', component: lazyRouteComponent(() => import('@/features/auth/ForgotPasswordPage'), 'ForgotPasswordPage') });
const resetRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/reset-password',
  validateSearch: (s: Record<string, unknown>) => ({ token: typeof s.token === 'string' ? s.token : '' }),
  component: lazyRouteComponent(() => import('@/features/auth/ResetPasswordPage'), 'ResetPasswordPage'),
});

const verifyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/verify-email/$token', component: lazyRouteComponent(() => import('@/features/auth/VerifyEmailPage'), 'VerifyEmailPage') });

const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/privacy', component: lazyRouteComponent(() => import('@/features/legal/LegalPage'), 'PrivacyPage') });
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: lazyRouteComponent(() => import('@/features/legal/LegalPage'), 'TermsPage') });

const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/invite/$token', component: lazyRouteComponent(() => import('@/features/household/InvitePage'), 'InvitePage') });

const authedRoute = createRoute({ getParentRoute: () => rootRoute, id: 'authed', component: AuthedLayout });
const homeRoute = createRoute({ getParentRoute: () => authedRoute, path: '/', component: lazyRouteComponent(() => import('@/features/home/HomePage'), 'HomePage') });
const txRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/transactions',
  validateSearch: parseFilters,
  component: lazyRouteComponent(() => import('@/features/transactions/TransactionsPage'), 'TransactionsPage'),
});
const budgetsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/budgets',
  component: lazyRouteComponent(() => import('@/features/budgets/BudgetsPage'), 'BudgetsPage'),
});
const mockCheckoutRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/mock-checkout',
  validateSearch: (s: Record<string, unknown>) => ({ token: typeof s.token === 'string' ? s.token : '' }),
  component: lazyRouteComponent(() => import('@/features/billing/MockCheckoutPage'), 'MockCheckoutPage'),
});
const mockPortalRoute = createRoute({ getParentRoute: () => authedRoute, path: '/mock-portal', component: lazyRouteComponent(() => import('@/features/billing/MockPortalPage'), 'MockPortalPage') });
const reportsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/reports', component: lazyRouteComponent(() => import('@/features/reports/ReportsPage'), 'ReportsPage') });
const goalsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/goals', component: lazyRouteComponent(() => import('@/features/goals/GoalsPage'), 'GoalsPage') });
const billsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/bills', component: lazyRouteComponent(() => import('@/features/bills/BillsPage'), 'BillsPage') });
const accountsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/accounts', component: lazyRouteComponent(() => import('@/features/accounts/AccountsPage'), 'AccountsPage') });
const categoriesRoute = createRoute({ getParentRoute: () => authedRoute, path: '/categories', component: lazyRouteComponent(() => import('@/features/categories/CategoriesPage'), 'CategoriesPage') });
const householdRoute = createRoute({ getParentRoute: () => authedRoute, path: '/household', component: lazyRouteComponent(() => import('@/features/household/HouseholdPage'), 'HouseholdPage') });
const importRoute = createRoute({ getParentRoute: () => authedRoute, path: '/import', component: lazyRouteComponent(() => import('@/features/import/ImportPage'), 'ImportPage') });
const moreRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/more',
  // Where checkout sends the user back to: ?checkout=success or ?checkout=canceled.
  validateSearch: (s: Record<string, unknown>): { checkout?: 'success' | 'canceled' } => (s.checkout === 'success' || s.checkout === 'canceled' ? { checkout: s.checkout } : {}),
  component: lazyRouteComponent(() => import('@/features/settings/MorePage'), 'MorePage'),
});

const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([loginRoute, registerRoute, forgotRoute, resetRoute]),
  verifyRoute,
  privacyRoute,
  termsRoute,
  inviteRoute,
  authedRoute.addChildren([homeRoute, txRoute, budgetsRoute, reportsRoute, goalsRoute, billsRoute, accountsRoute, categoriesRoute, householdRoute, importRoute, mockCheckoutRoute, mockPortalRoute, moreRoute]),
]);

/** Factory so tests can start at any path with an in-memory history. */
export function createAppRouter(history?: RouterHistory) {
  return createRouter({ routeTree, defaultPreload: 'intent', ...(history ? { history } : {}) });
}

export const router = createAppRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
